import { formatEuro, formatSquareMeters } from '@brimo/shared';
import type { PillVariant } from '../../ds/components';
import type { ReservationStatus, UnitStatus } from './api';

/** Pill of a unit status (Objekte 03/04: Frei sand · Reserviert accent · Verkauft success). */
export const unitStatusLabels: Record<UnitStatus, string> = {
  available: 'Frei',
  reserved: 'Reserviert',
  sold: 'Verkauft',
  unavailable: 'Nicht verfügbar',
};

export const unitStatusVariants: Record<UnitStatus, PillVariant> = {
  available: 'neutral',
  reserved: 'accent',
  sold: 'success',
  unavailable: 'warning',
};

/** Pill tone per reservation status; the label itself is the API's `statusLabel`. */
export const reservationStatusVariants: Record<ReservationStatus, PillVariant> = {
  awaiting_signature: 'warning',
  awaiting_approval: 'warning',
  active: 'accent',
  rejected: 'danger',
  withdrawn: 'neutral',
  released: 'neutral',
};

/** „98,4 m² · 3 Zimmer · 2. OG · 498.000 €" from whatever the row carries. */
export function unitLine(unit: {
  areaSqm?: number | null;
  rooms?: number | string | null;
  floor?: string | null;
  priceCents?: number | null;
}): string {
  const parts: string[] = [];
  if (unit.areaSqm !== null && unit.areaSqm !== undefined) parts.push(formatSquareMeters(unit.areaSqm));
  if (unit.rooms !== null && unit.rooms !== undefined && unit.rooms !== '' && unit.rooms !== '—') {
    parts.push(`${unit.rooms} Zimmer`);
  }
  if (unit.floor) parts.push(unit.floor);
  if (unit.priceCents !== null && unit.priceCents !== undefined) parts.push(formatEuro(unit.priceCents));
  return parts.join(' · ');
}

/** „SB" for „Stefan Brunnthaler" (avatar of contact hits). */
export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.charAt(0) ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.charAt(0) ?? '') : '';
  return `${first}${last}`.toUpperCase() || '?';
}

/** „TG 14 · 26.000 €" / „TG 14" / „—". */
export function parkingSpaceLine(space: { number: string; priceCents: number | null } | null): string {
  if (space === null) return '—';
  return space.priceCents === null ? space.number : `${space.number} · ${formatEuro(space.priceCents)}`;
}

/** „305 € mtl." / „—". */
export function serviceChargeLine(cents: number | null): string {
  return cents === null ? '—' : `${formatEuro(cents)} mtl.`;
}

/** Days as the screens count them („noch 24 s" → seconds here): whole seconds left until `iso`. */
export function secondsUntil(iso: string, now = Date.now()): number {
  return Math.max(0, Math.ceil((new Date(iso).getTime() - now) / 1000));
}
